/**
 * DocRow — one document row in the HubList.
 *
 * Compact horizontal layout:
 *   [type-badge] filename · ConfPill · doctype-badge · [blocking?] · size · ›
 *
 * The DocumentRow data model (from M2) carries everything the row
 * needs — strategy doc_type + Spec 32 source_doctype + per-tag
 * confidences + verified flag + blocking flag + low flag.
 *
 * Click → fires onClick(doc); HubDetail (M6) is the eventual
 * destination. The row itself is purely presentational; the
 * parent owns selection state.
 */

import { Button, makeStyles, mergeClasses } from "@fluentui/react-components";
import { ArrowDownload24Regular, Delete24Regular } from "@fluentui/react-icons";
import type React from "react";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../../tokens";
import { statusLane } from "../tokens";
import type { DocumentRow } from "../types";
import { AIReadinessChip } from "./AIReadinessChip";
import { ConfidenceBar } from "./ConfidenceBar";
import { EvidenceSignals } from "./EvidenceSignals";

export interface DocRowProps {
  row: DocumentRow;
  onClick?: (row: DocumentRow) => void;
  onDeleteDoc?: (row: DocumentRow) => void;
  onDownloadDoc?: (row: DocumentRow) => void;
}

const useStyles = makeStyles({
  rowButton: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: 0,
    backgroundColor: "transparent",
    border: "none",
    borderBottom: `1px solid ${surfaceColors.line}`,
    // The 2px status lane is a left-border on the button itself so
    // it spans the full row height without overflow-clip concerns.
    borderLeft: "2px solid transparent",
    cursor: "pointer",
    fontFamily: fonts.body,
    ":hover": {
      backgroundColor: brandColors.light,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-2px",
    },
  },
  inner: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "8px",
    padding: "9px 14px",
  },
  fileBadge: {
    width: "20px",
    height: "22px",
    borderRadius: "2px",
    marginTop: "1px",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    paddingBottom: "2px",
    fontSize: "6.5px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    flexShrink: 0,
    border: "1px solid transparent",
  },
  fileBadgeUnverified: {
    backgroundColor: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.lineStrong}`,
    color: inkColors.ink4,
  },
  fileBadgeVerified: {
    backgroundColor: brandColors.light,
    border: `1px solid ${brandColors.mid}`,
    color: brandColors.primary,
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
  },
  filename: {
    fontSize: "11.5px",
    fontWeight: 600,
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginBottom: "3px",
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "5px",
    rowGap: "3px",
    flexWrap: "wrap",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "3px",
    padding: "1px 5px",
    borderRadius: "3px",
    fontSize: "8.5px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    border: "1px solid transparent",
  },
  pillVerified: {
    backgroundColor: "#E6FBF3",
    border: `1px solid ${statusColors.cool}`,
    color: "#0F5132",
  },
  pillHigh: {
    backgroundColor: "#E6FBF3",
    border: `1px solid ${statusColors.cool}`,
    color: "#0F5132",
  },
  pillMid: {
    backgroundColor: "#FEF6E3",
    border: `1px solid ${statusColors.warn}`,
    color: "#7C2D12",
  },
  pillLow: {
    backgroundColor: "#FCE7EC",
    border: `1px solid ${statusColors.danger}`,
    color: statusColors.danger,
  },
  pillLimited: {
    backgroundColor: "#FEF6E3",
    border: `1px solid ${statusColors.warn}`,
    color: "#7C2D12",
  },
  pillFailed: {
    backgroundColor: "#FCE7EC",
    border: `1px solid ${statusColors.danger}`,
    color: statusColors.danger,
  },
  doctypeBadge: {
    fontSize: "9.5px",
    fontWeight: 600,
    padding: "1px 6px",
    borderRadius: "3px",
    backgroundColor: brandColors.light,
    color: brandColors.primary,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  blockingBadge: {
    fontSize: "9.5px",
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: "3px",
    backgroundColor: "#FCE7EC",
    color: statusColors.danger,
    border: `1px solid ${statusColors.danger}`,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  sizeText: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: inkColors.ink5,
  },
  paramRow: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "6px",
    rowGap: "3px",
    marginTop: "4px",
  },
  paramChip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "3px",
    padding: "1px 5px",
    borderRadius: "3px",
    fontSize: "8.5px",
    fontFamily: fonts.mono,
    backgroundColor: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.lineStrong}`,
    color: inkColors.ink3,
  },
  paramChipKey: {
    color: inkColors.ink4,
  },
  paramChipValue: {
    color: inkColors.ink,
    fontWeight: 600,
  },
  chevron: {
    marginTop: "4px",
    flexShrink: 0,
    color: inkColors.ink4,
  },
  deleteButton: {
    marginTop: "4px",
    marginLeft: "6px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    columnGap: "3px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    color: statusColors.danger,
    fontSize: "10px",
    fontWeight: 600,
    fontFamily: fonts.body,
    ":hover": {
      color: brandColors.primary,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  downloadButton: {
    marginTop: "4px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    columnGap: "3px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    color: inkColors.ink4,
    fontSize: "10px",
    fontWeight: 600,
    fontFamily: fonts.body,
    ":hover": {
      color: brandColors.primary,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  // ── Phase 2: AI intelligence additions ──────────────────────────
  filenameRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "4px",
    marginBottom: "3px",
  },
  confidenceBarWrap: {
    marginTop: "4px",
  },
});

function fileExtensionShort(filename: string): string {
  const idx = filename.lastIndexOf(".");
  if (idx === -1 || idx === filename.length - 1) return "FILE";
  return filename.slice(idx + 1, idx + 4).toUpperCase();
}

function ConfPill(props: { row: DocumentRow }): React.ReactElement {
  const styles = useStyles();
  const { row } = props;

  // Verified always wins — once a writer has signed off, the
  // confidence pill flips to a unified "OK" badge regardless of
  // the underlying classifier confidence.
  if (row.verified) {
    return (
      <span className={mergeClasses(styles.pill, styles.pillVerified)} aria-label="verified">
        ✓ ok
      </span>
    );
  }

  if (row.processing_status === "limited") {
    return (
      <span className={mergeClasses(styles.pill, styles.pillLimited)} aria-label="text not indexed">
        ⚠ no text
      </span>
    );
  }
  if (row.processing_status === "failed_upload" || row.processing_status === "failed_processing") {
    return (
      <span className={mergeClasses(styles.pill, styles.pillFailed)} aria-label="failed">
        ✕ failed
      </span>
    );
  }

  // Source-doctype confidence preferred over strategy confidence
  // when both are present (the M6 detail view will surface BOTH).
  const sourceConf = row.source_classification?.source_doctype_confidence;
  if (typeof sourceConf === "number") {
    const pct = Math.round(sourceConf * 100);
    let cls = styles.pillLow;
    if (sourceConf >= 0.85) cls = styles.pillHigh;
    else if (sourceConf >= 0.6) cls = styles.pillMid;
    return (
      <span className={mergeClasses(styles.pill, cls)} aria-label={`confidence ${pct} percent`}>
        {pct}%
      </span>
    );
  }

  // Fallback to strategy confidence when no source-doctyping signal.
  const fallback = row.classification_confidence;
  if (fallback === "high") {
    return <span className={mergeClasses(styles.pill, styles.pillHigh)}>high</span>;
  }
  if (fallback === "medium") {
    return <span className={mergeClasses(styles.pill, styles.pillMid)}>med</span>;
  }
  if (fallback === "low") {
    return <span className={mergeClasses(styles.pill, styles.pillLow)}>low</span>;
  }
  return <span className={mergeClasses(styles.pill, styles.pillMid)}>?</span>;
}

export const DocRow: React.FC<DocRowProps> = ({ row, onClick, onDeleteDoc, onDownloadDoc }) => {
  const styles = useStyles();
  const handleClick = (): void => onClick?.(row);
  const handleDelete = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onDeleteDoc?.(row);
  };
  const handleDownload = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onDownloadDoc?.(row);
  };
  // ``doc_id`` is null only while an upload's POST is still in flight — there's
  // no stored file to pull yet, so hide the download affordance until it lands.
  const canDownload = Boolean(onDownloadDoc && row.doc_id);
  const showBlocking = row.blocking && !row.verified;
  const laneColor = statusLane[row.processing_status as string] ?? "transparent";
  return (
    // A plain <div role="button"> rather than a real <button> — the
    // delete affordance below is itself a <button>, and nesting
    // interactive controls (<button> inside <button>) is invalid HTML
    // that some WebViews (this ships inside an Office Add-in task
    // pane) dispatch inconsistently, occasionally double-firing the
    // inner control's click.
    <button
      type="button"
      tabIndex={0}
      className={styles.rowButton}
      style={{ borderLeftColor: laneColor }}
      onClick={handleClick}
      data-testid={`doc-row-${row.doc_id ?? row.filename}`}
      aria-label={`Document ${row.filename}`}
    >
      <div className={styles.inner}>
        <div
          className={mergeClasses(
            styles.fileBadge,
            row.verified ? styles.fileBadgeVerified : styles.fileBadgeUnverified,
          )}
          aria-hidden="true"
        >
          {fileExtensionShort(row.filename)}
        </div>
        <div className={styles.rowMain}>
          <div className={styles.filenameRow}>
            <span className={styles.filename} style={{ marginBottom: 0, flex: 1, minWidth: 0 }}>
              {row.filename}
            </span>
            <AIReadinessChip row={row} variant="row" />
          </div>
          <div className={styles.metaRow}>
            <ConfPill row={row} />
            {row.source_classification?.source_doctype && (
              <span className={styles.doctypeBadge}>{row.source_classification.source_doctype}</span>
            )}
            {!row.source_classification?.source_doctype && row.doc_type && (
              <span className={styles.doctypeBadge}>{row.doc_type}</span>
            )}
            {showBlocking && (
              <span className={styles.blockingBadge} aria-label="blocking">
                blocking
              </span>
            )}
            {row.source_classification && <EvidenceSignals row={row} />}
          </div>
          {typeof row.source_classification?.source_doctype_confidence === "number" && (
            <div className={styles.confidenceBarWrap}>
              <ConfidenceBar confidence={row.source_classification.source_doctype_confidence} size="sm" />
            </div>
          )}
          {row.label_params && Object.entries(row.label_params).some(([, v]) => v !== null) && (
            <div className={styles.paramRow} data-testid="doc-row-params">
              {Object.entries(row.label_params)
                .filter(([, v]) => v !== null)
                .map(([key, value]) => (
                  <span key={key} className={styles.paramChip}>
                    <span className={styles.paramChipKey}>{key}:</span>
                    <span className={styles.paramChipValue}>{value}</span>
                  </span>
                ))}
            </div>
          )}
        </div>
        {canDownload && (
          <button
            type="button"
            className={styles.downloadButton}
            onClick={handleDownload}
            aria-label={`Download document ${row.filename}`}
            title="Download this document"
          >
            <ArrowDownload24Regular aria-hidden="true" className={"w-5 h-5"} />
          </button>
        )}
        {onDeleteDoc && (
          <button
            type="button"
            className={styles.deleteButton}
            onClick={handleDelete}
            aria-label={`Delete document ${row.filename}`}
            title="Delete this document"
          >
            <Delete24Regular aria-hidden="true" className={"w-5 h-5"} />
          </button>
        )}
      </div>
    </button>
  );
};

export default DocRow;
